import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  CheckCircle2,
  LoaderCircle,
  RefreshCw,
  Save,
  Scale,
  Trash2,
} from 'lucide-react'
import { api, getApiErrorMessage } from '../../lib/api'
import { asArray } from '../../lib/collections'

const SECTION_LABELS = {
  DISCOVERY: '탐색',
  ACCURACY: '정확성',
  EXPLANATION: '설명',
  EMPATHY: '공감',
  COMPLIANCE: '준법',
  CLOSING: '마무리',
  DIALOGUE_BALANCE: '대화 균형',
}

function unwrap(response) {
  return response?.data?.data ?? response?.data ?? null
}

function formatNumber(value, suffix = '') {
  return `${new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 1 }).format(Number(value) || 0)}${suffix}`
}

function formatDate(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat('ko-KR', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

function scoreValue(value, fallback = 0) {
  const number = Number(value)
  return Number.isFinite(number) ? Math.max(0, Math.min(100, number)) : fallback
}

function formFromDetail(detail) {
  if (!detail) return null
  const humanSections = new Map(
    (Array.isArray(detail.humanSections) ? detail.humanSections : [])
      .map((item) => [item.code, item]),
  )
  const sections = (Array.isArray(detail.aiSections) ? detail.aiSections : [])
    .filter((item) => item?.applicable !== false && item?.code)
    .map((item) => ({
      code: item.code,
      label: item.label || SECTION_LABELS[item.code] || '평가 항목',
      aiScore: scoreValue(item.score),
      score: scoreValue(humanSections.get(item.code)?.score, scoreValue(item.score)),
    }))
  return {
    overallScore: scoreValue(detail.humanScore, scoreValue(detail.aiScore)),
    safetyStatus: detail.humanSafety || detail.aiSafety || 'PASS',
    comment: detail.comment || '',
    sections,
  }
}

function Metric({ label, value, detail }) {
  return (
    <div className="min-w-0 px-4 py-4">
      <div className="text-xs font-medium text-slate-500">{label}</div>
      <div className="mt-1 text-xl font-semibold text-slate-950">{value}</div>
      {detail && <div className="mt-1 text-xs text-slate-500">{detail}</div>}
    </div>
  )
}

export default function CounselPracticeCalibration() {
  const [data, setData] = useState(null)
  const [selectedId, setSelectedId] = useState(null)
  const [detail, setDetail] = useState(null)
  const [form, setForm] = useState(null)
  const [loading, setLoading] = useState(true)
  const [detailLoading, setDetailLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const load = useCallback(async ({ keepSelection = true } = {}) => {
    setLoading(true)
    setError('')
    try {
      const response = await api.get('/counsel-practice/admin/calibration', {
        params: { days: 90, limit: 80 },
        __silent: true,
      })
      const loaded = unwrap(response) || {}
      const items = Array.isArray(loaded.items) ? loaded.items : []
      setData({ ...loaded, items })
      setSelectedId((current) => (
        keepSelection && items.some((item) => item.sessionId === current)
          ? current
          : items[0]?.sessionId || null
      ))
      if (items.length === 0) {
        setDetail(null)
        setForm(null)
      }
    } catch (e) {
      setError(getApiErrorMessage(e, '점수 보정 자료를 불러오지 못했습니다.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load({ keepSelection: false })
  }, [load])

  useEffect(() => {
    if (!selectedId) return undefined
    let mounted = true
    const loadDetail = async () => {
      setDetailLoading(true)
      setError('')
      setNotice('')
      try {
        const response = await api.get(`/counsel-practice/admin/calibration/sessions/${selectedId}`, {
          __silent: true,
        })
        if (!mounted) return
        const loaded = unwrap(response)
        setDetail(loaded)
        setForm(formFromDetail(loaded))
      } catch (e) {
        if (mounted) setError(getApiErrorMessage(e, '평가할 상담 기록을 불러오지 못했습니다.'))
      } finally {
        if (mounted) setDetailLoading(false)
      }
    }
    loadDetail()
    return () => { mounted = false }
  }, [selectedId])

  const selected = useMemo(
    () => asArray(data?.items).find((item) => item.sessionId === selectedId) || null,
    [data, selectedId],
  )
  const summary = data?.summary || {}

  const save = async () => {
    if (!detail || !form || saving) return
    setSaving(true)
    setError('')
    setNotice('')
    try {
      const response = await api.put(
        `/counsel-practice/admin/calibration/sessions/${detail.sessionId}`,
        {
          overallScore: scoreValue(form.overallScore),
          sectionScores: form.sections.map((item) => ({
            code: item.code,
            score: scoreValue(item.score),
          })),
          safetyStatus: form.safetyStatus,
          comment: form.comment.trim(),
        },
        { __silent: true },
      )
      const saved = unwrap(response)
      setDetail(saved)
      setForm(formFromDetail(saved))
      setNotice('사람 평가를 저장했습니다.')
      await load()
    } catch (e) {
      setError(getApiErrorMessage(e, '사람 평가를 저장하지 못했습니다.'))
    } finally {
      setSaving(false)
    }
  }

  const remove = async () => {
    if (!detail?.reviewed || saving) return
    if (!window.confirm('저장한 사람 평가를 삭제할까요?')) return
    setSaving(true)
    setError('')
    setNotice('')
    try {
      await api.delete(`/counsel-practice/admin/calibration/sessions/${detail.sessionId}`, {
        __silent: true,
      })
      setNotice('사람 평가를 삭제했습니다.')
      await load()
      const response = await api.get(`/counsel-practice/admin/calibration/sessions/${detail.sessionId}`, {
        __silent: true,
      })
      const loaded = unwrap(response)
      setDetail(loaded)
      setForm(formFromDetail(loaded))
    } catch (e) {
      setError(getApiErrorMessage(e, '사람 평가를 삭제하지 못했습니다.'))
    } finally {
      setSaving(false)
    }
  }

  if (loading && !data) {
    return (
      <div className="flex min-h-[28rem] items-center justify-center text-slate-500">
        <LoaderCircle className="h-6 w-6 animate-spin" aria-label="점수 보정 자료 불러오는 중" />
      </div>
    )
  }

  return (
    <section className="mt-5" data-testid="counsel-calibration" aria-labelledby="counsel-calibration-title">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-4">
        <div>
          <h2 id="counsel-calibration-title" className="flex items-center gap-2 text-lg font-semibold text-slate-950">
            <Scale className="h-5 w-5 text-teal-700" aria-hidden="true" />
            점수 보정
          </h2>
          <p className="mt-1 text-sm text-slate-600">AI 평가가 사람 평가와 얼마나 다른지 확인하고 점수 기준을 다듬습니다.</p>
        </div>
        <button
          type="button"
          onClick={() => load()}
          disabled={loading || saving}
          className="flex h-9 w-9 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          title="새로고침"
          aria-label="점수 보정 자료 새로고침"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
        </button>
      </div>

      {error && <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700" role="alert">{error}</div>}
      {notice && <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800" role="status">{notice}</div>}

      <div className="mt-5 grid overflow-hidden border border-slate-200 bg-white sm:grid-cols-2 xl:grid-cols-5">
        <Metric label="사람 평가" value={formatNumber(summary.reviewedCount, '건')} detail={summary.basisLabel} />
        <Metric label="동료 평가" value={formatNumber(summary.independentReviewCount, '건')} detail="20건부터 기준을 판단합니다" />
        <Metric label="평균 점수 차이" value={formatNumber(summary.meanAbsoluteError, '점')} detail={`AI 치우침 ${Number(summary.meanBias) > 0 ? '+' : ''}${formatNumber(summary.meanBias, '점')}`} />
        <Metric label="5점 이내 일치" value={formatNumber(summary.withinFiveRate, '%')} />
        <Metric label="준법 판단 일치" value={formatNumber(summary.safetyAgreementRate, '%')} detail={summary.statusLabel} />
      </div>

      <div className="mt-6 grid min-h-[640px] gap-6 xl:grid-cols-[20rem_minmax(0,1fr)]">
        <aside className="min-w-0 border-r border-slate-200 pr-0 xl:pr-5" aria-label="평가할 상담 기록">
          <div className="flex items-center justify-between pb-3">
            <h3 className="font-semibold text-slate-950">상담 기록</h3>
            <span className="text-xs text-slate-500">최근 90일</span>
          </div>
          <div className="max-h-[760px] overflow-y-auto border-y border-slate-200">
            {(asArray(data?.items)).map((item) => (
              <button
                key={item.sessionId}
                type="button"
                onClick={() => setSelectedId(item.sessionId)}
                className={`block w-full border-b border-slate-100 px-3 py-3 text-left last:border-b-0 ${
                  item.sessionId === selectedId ? 'bg-sky-50' : 'bg-white hover:bg-slate-50'
                }`}
                aria-pressed={item.sessionId === selectedId}
              >
                <div className="flex items-start justify-between gap-3">
                  <span className="line-clamp-2 text-sm font-semibold text-slate-900">{item.contentTitle}</span>
                  <span className="shrink-0 text-sm font-semibold text-sky-700">{formatNumber(item.aiScore, '점')}</span>
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
                  <span>{item.plannerLabel}</span>
                  <span>{item.contentTypeLabel}</span>
                  <span>{formatDate(item.completedAt)}</span>
                </div>
                {item.reviewed && (
                  <div className="mt-2 flex items-center gap-1 text-xs font-medium text-emerald-700">
                    <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                    {item.reviewScopeLabel} 완료
                  </div>
                )}
              </button>
            ))}
            {(asArray(data?.items)).length === 0 && (
              <p className="px-4 py-10 text-center text-sm text-slate-500">평가가 끝난 상담 기록이 없습니다.</p>
            )}
          </div>
        </aside>

        <div className="min-w-0">
          {detailLoading ? (
            <div className="flex min-h-[30rem] items-center justify-center text-slate-500">
              <LoaderCircle className="h-6 w-6 animate-spin" aria-label="상담 기록 불러오는 중" />
            </div>
          ) : detail && form ? (
            <>
              <header className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 pb-4">
                <div className="min-w-0">
                  <div className="text-xs font-medium text-slate-500">{detail.plannerLabel} · {detail.contentTypeLabel}</div>
                  <h3 className="mt-1 text-base font-semibold text-slate-950">{detail.contentTitle}</h3>
                </div>
                <div className="text-right">
                  <div className="text-xs text-slate-500">AI 평가</div>
                  <div className="mt-1 text-xl font-semibold text-sky-700">{formatNumber(detail.aiScore, '점')}</div>
                </div>
              </header>

              <section className="mt-5" aria-labelledby="calibration-transcript-title">
                <h4 id="calibration-transcript-title" className="text-sm font-semibold text-slate-900">상담 내용</h4>
                <div className="mt-3 max-h-72 space-y-3 overflow-y-auto border-y border-slate-200 py-4">
                  {(asArray(detail?.turns)).map((turn) => (
                    <div key={`${turn.turnNo}-${turn.speaker}`} className="grid gap-1 px-2 sm:grid-cols-[4rem_minmax(0,1fr)] sm:gap-3">
                      <span className={`text-xs font-semibold ${turn.speaker === 'PLANNER' ? 'text-sky-700' : 'text-slate-500'}`}>{turn.speakerLabel}</span>
                      <p className="whitespace-pre-wrap break-words text-sm leading-6 text-slate-800">{turn.text}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section className="mt-6" aria-labelledby="human-review-title">
                <div className="flex flex-wrap items-end justify-between gap-3">
                  <div>
                    <h4 id="human-review-title" className="text-sm font-semibold text-slate-900">사람 평가</h4>
                    <p className="mt-1 text-xs text-slate-500">{selected?.plannerLabel === '내 연습' ? '본인 재평가로 집계됩니다.' : '동료 재평가로 집계됩니다.'}</p>
                  </div>
                  <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                    총점
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={form.overallScore}
                      onChange={(event) => setForm((current) => ({ ...current, overallScore: event.target.value }))}
                      className="h-9 w-24 rounded-md border border-slate-300 px-3 text-right text-sm"
                      aria-label="사람 평가 총점"
                    />
                  </label>
                </div>

                <div className="mt-4 overflow-x-auto border-y border-slate-200">
                  <table className="w-full min-w-[520px] text-left text-sm">
                    <thead className="bg-slate-50 text-xs text-slate-500">
                      <tr><th className="px-4 py-3 font-medium">평가 항목</th><th className="px-4 py-3 text-right font-medium">AI 점수</th><th className="px-4 py-3 text-right font-medium">사람 점수</th><th className="px-4 py-3 text-right font-medium">차이</th></tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {form.sections.map((section, index) => (
                        <tr key={section.code}>
                          <td className="px-4 py-3 font-medium text-slate-900">{section.label}</td>
                          <td className="px-4 py-3 text-right text-slate-600">{formatNumber(section.aiScore, '점')}</td>
                          <td className="px-4 py-2 text-right">
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="0.1"
                              value={section.score}
                              onChange={(event) => setForm((current) => ({
                                ...current,
                                sections: current.sections.map((item, itemIndex) => (
                                  itemIndex === index ? { ...item, score: event.target.value } : item
                                )),
                              }))}
                              className="h-9 w-24 rounded-md border border-slate-300 px-3 text-right text-sm"
                              aria-label={`${section.label} 사람 점수`}
                            />
                          </td>
                          <td className="px-4 py-3 text-right font-medium text-slate-800">{formatNumber(scoreValue(section.aiScore) - scoreValue(section.score), '점')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <fieldset className="mt-5">
                  <legend className="text-sm font-medium text-slate-700">준법 판단</legend>
                  <div className="mt-2 inline-flex overflow-hidden rounded-md border border-slate-300 bg-white" role="group">
                    {[
                      ['PASS', '문제 없음'],
                      ['WARN', '주의 필요'],
                      ['FAIL', '중대한 문제'],
                    ].map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setForm((current) => ({ ...current, safetyStatus: value }))}
                        className={`h-9 border-r border-slate-300 px-3 text-sm font-medium last:border-r-0 ${
                          form.safetyStatus === value ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'
                        }`}
                        aria-pressed={form.safetyStatus === value}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </fieldset>

                <label className="mt-5 block">
                  <span className="text-sm font-medium text-slate-700">검토 메모</span>
                  <textarea
                    value={form.comment}
                    maxLength={500}
                    onChange={(event) => setForm((current) => ({ ...current, comment: event.target.value }))}
                    rows={3}
                    className="mt-2 w-full resize-y rounded-md border border-slate-300 px-3 py-2 text-sm leading-6"
                    placeholder="점수가 달라진 이유를 남겨 주세요."
                  />
                </label>

                <div className="mt-5 flex justify-end gap-2">
                  {detail.reviewed && (
                    <button
                      type="button"
                      onClick={remove}
                      disabled={saving}
                      className="inline-flex h-10 items-center gap-2 rounded-md border border-rose-300 bg-white px-4 text-sm font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                      평가 삭제
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={save}
                    disabled={saving || form.sections.length === 0}
                    className="inline-flex h-10 items-center gap-2 rounded-md bg-slate-900 px-4 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
                  >
                    {saving ? <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Save className="h-4 w-4" aria-hidden="true" />}
                    평가 저장
                  </button>
                </div>
              </section>
            </>
          ) : (
            <div className="flex min-h-[30rem] items-center justify-center text-sm text-slate-500">평가할 상담 기록을 선택해 주세요.</div>
          )}
        </div>
      </div>
    </section>
  )
}
